import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
    Search, Upload, Download, Trash2, FileText, FileImage, FileArchive,
    Presentation, File, FolderOpen,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const ACCEPT = ".pdf,.ppt,.pptx,.doc,.docx,.zip,.png,.jpg,.jpeg,.webp,.gif";
const CATEGORIES = [
    { value: "general", label: "General" },
    { value: "slides", label: "Lecture Slides" },
    { value: "reading", label: "Reading" },
    { value: "reference", label: "Reference" },
    { value: "notes", label: "Notes" },
];

function typeIcon(type) {
    switch (type) {
        case "PDF": return FileText;
        case "PPT": return Presentation;
        case "DOCX": return FileText;
        case "ZIP": return FileArchive;
        case "IMAGE": return FileImage;
        default: return File;
    }
}

function formatSize(bytes) {
    if (!bytes && bytes !== 0) return "—";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function Resources() {
    const { user } = useAuth();
    const canManage = user.role === "ADMIN" || user.role === "FACULTY";

    const [items, setItems] = useState([]);
    const [courses, setCourses] = useState([]);
    const [q, setQ] = useState("");
    const [courseFilter, setCourseFilter] = useState("");

    const [open, setOpen] = useState(false);
    const [file, setFile] = useState(null);
    const [form, setForm] = useState({ title: "", category: "general", course_id: "", lesson_id: "" });
    const [lessons, setLessons] = useState([]);
    const [uploading, setUploading] = useState(false);

    const load = async () => {
        const params = new URLSearchParams();
        if (q) params.append("q", q);
        if (courseFilter) params.append("course_id", courseFilter);
        const { data } = await api.get(`/resources?${params}`);
        setItems(data.items);
    };
    useEffect(() => { load(); }, [q, courseFilter]); // eslint-disable-line
    useEffect(() => { api.get("/courses").then(r => setCourses(r.data.items)).catch(() => {}); }, []);

    // When a course is chosen in the upload dialog, load its lessons for optional attachment.
    useEffect(() => {
        if (!form.course_id) { setLessons([]); return; }
        api.get(`/courses/${form.course_id}`).then(({ data }) => {
            const flat = (data.modules || []).flatMap(m =>
                (m.lessons || []).map(l => ({ id: l.id, label: `${m.title} · ${l.title}` }))
            );
            setLessons(flat);
        }).catch(() => setLessons([]));
    }, [form.course_id]);

    const courseName = (id) => courses.find(c => c.id === id)?.title;

    const resetForm = () => { setForm({ title: "", category: "general", course_id: "", lesson_id: "" }); setFile(null); setLessons([]); };

    const upload = async () => {
        if (!file) return;
        const fd = new FormData();
        fd.append("file", file);
        if (form.title) fd.append("title", form.title);
        if (form.category) fd.append("category", form.category);
        if (form.course_id) fd.append("course_id", form.course_id);
        if (form.lesson_id) fd.append("lesson_id", form.lesson_id);
        setUploading(true);
        try {
            await api.post("/resources/upload", fd);
            toast.success("Resource uploaded");
            setOpen(false);
            resetForm();
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Upload failed");
        } finally {
            setUploading(false);
        }
    };

    const download = async (r) => {
        try {
            const resp = await api.get(`/resources/${r.id}/download`, { responseType: "blob" });
            const url = URL.createObjectURL(resp.data);
            const a = document.createElement("a");
            a.href = url; a.download = r.filename || r.title || "resource"; a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            const detail = err.response?.status === 403 ? "You don't have access to this resource" : "Download failed";
            toast.error(detail);
        }
    };

    const remove = async (r) => {
        if (!window.confirm(`Delete "${r.title}"? This cannot be undone.`)) return;
        try {
            await api.delete(`/resources/${r.id}`);
            toast.success("Resource deleted");
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Delete failed");
        }
    };

    return (
        <div className="space-y-6" data-testid="resources-page">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <div className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Library</div>
                    <h1 className="font-display text-3xl lg:text-4xl mt-1">Resources</h1>
                </div>
                {canManage && (
                    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetForm(); }}>
                        <DialogTrigger asChild>
                            <Button className="mx-btn-primary" data-testid="upload-resource-btn"><Upload className="h-4 w-4 mr-2" /> Upload resource</Button>
                        </DialogTrigger>
                        <DialogContent>
                            <DialogHeader>
                                <DialogTitle>Upload a resource</DialogTitle>
                                <DialogDescription>Allowed: PDF, PPT(X), DOC(X), ZIP, images — max 50 MB. Attaching to a course/lesson is optional.</DialogDescription>
                            </DialogHeader>
                            <div className="space-y-4">
                                <div>
                                    <Label>File</Label>
                                    <Input type="file" accept={ACCEPT} onChange={(e) => setFile(e.target.files[0])} data-testid="resource-file-input" />
                                </div>
                                <div>
                                    <Label>Title <span className="text-muted-foreground font-normal">(optional — defaults to filename)</span></Label>
                                    <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="e.g. Week 1 lecture slides" data-testid="resource-title-input" />
                                </div>
                                <div>
                                    <Label>Category</Label>
                                    <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                                        <SelectTrigger data-testid="resource-category-select"><SelectValue /></SelectTrigger>
                                        <SelectContent>{CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label>Course <span className="text-muted-foreground font-normal">(optional)</span></Label>
                                    <Select value={form.course_id || "NONE"} onValueChange={(v) => setForm({ ...form, course_id: v === "NONE" ? "" : v, lesson_id: "" })}>
                                        <SelectTrigger data-testid="resource-course-select"><SelectValue placeholder="No course" /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="NONE">No course</SelectItem>
                                            {courses.map(c => <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                                {form.course_id && lessons.length > 0 && (
                                    <div>
                                        <Label>Lesson <span className="text-muted-foreground font-normal">(optional)</span></Label>
                                        <Select value={form.lesson_id || "NONE"} onValueChange={(v) => setForm({ ...form, lesson_id: v === "NONE" ? "" : v })}>
                                            <SelectTrigger data-testid="resource-lesson-select"><SelectValue placeholder="No specific lesson" /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="NONE">No specific lesson</SelectItem>
                                                {lessons.map(l => <SelectItem key={l.id} value={l.id}>{l.label}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                )}
                            </div>
                            <DialogFooter>
                                <Button onClick={upload} disabled={!file || uploading} className="mx-btn-primary" data-testid="resource-upload-submit">
                                    {uploading ? "Uploading…" : "Upload"}
                                </Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                )}
            </div>

            <div className="flex flex-wrap gap-3 items-center">
                <div className="relative flex-1 max-w-sm">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by title" className="pl-9 rounded-xl" data-testid="resources-search" />
                </div>
                <Select value={courseFilter || "ALL"} onValueChange={(v) => setCourseFilter(v === "ALL" ? "" : v)}>
                    <SelectTrigger className="w-56" data-testid="resources-course-filter"><SelectValue placeholder="All courses" /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="ALL">All courses</SelectItem>
                        {courses.map(c => <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>)}
                    </SelectContent>
                </Select>
                <Badge variant="secondary">{items.length} {items.length === 1 ? "file" : "files"}</Badge>
            </div>

            <Card className="mx-card">
                <CardContent className="p-0 overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Resource</TableHead>
                                <TableHead>Type</TableHead>
                                <TableHead>Course</TableHead>
                                <TableHead>Size</TableHead>
                                <TableHead>Uploaded</TableHead>
                                <TableHead className="text-right w-32">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {items.map(r => {
                                const Icon = typeIcon(r.type);
                                return (
                                    <TableRow key={r.id} data-testid={`resource-row-${r.id}`}>
                                        <TableCell>
                                            <div className="flex items-center gap-3">
                                                <div className="h-9 w-9 rounded-xl bg-[#F0EFFF] dark:bg-[#1C1A2E] flex items-center justify-center shrink-0">
                                                    <Icon className="h-4 w-4 text-[#726AF0]" />
                                                </div>
                                                <div className="min-w-0">
                                                    <div className="font-semibold text-sm truncate">{r.title}</div>
                                                    <div className="text-xs text-muted-foreground truncate">{r.filename}</div>
                                                </div>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant="outline" className="text-[10px]">{r.type}</Badge>
                                            {r.category && r.category !== "general" && (
                                                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">{r.category}</div>
                                            )}
                                        </TableCell>
                                        <TableCell className="text-sm">{courseName(r.course_id) || <span className="text-muted-foreground">General</span>}</TableCell>
                                        <TableCell className="text-sm text-muted-foreground">{formatSize(r.size_bytes)}</TableCell>
                                        <TableCell className="text-sm text-muted-foreground">{r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}</TableCell>
                                        <TableCell>
                                            <div className="flex items-center justify-end gap-1">
                                                <Button variant="ghost" size="icon" onClick={() => download(r)} data-testid={`resource-download-${r.id}`} aria-label="Download">
                                                    <Download className="h-4 w-4" />
                                                </Button>
                                                {canManage && (
                                                    <Button variant="ghost" size="icon" onClick={() => remove(r)} data-testid={`resource-delete-${r.id}`} aria-label="Delete" className="text-[#E11D48] hover:text-[#E11D48]">
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                            {items.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={6} className="text-center text-muted-foreground py-12">
                                        <FolderOpen className="h-10 w-10 mx-auto mb-3 opacity-40" />
                                        {canManage ? "No resources yet. Upload your first file." : "No resources available yet."}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
        </div>
    );
}
